/**
 * Générateur — Automatisme : axes de symétrie d'une figure
 * Attendu ID : 1017 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « Il reconnaît si une figure
 * possède un ou plusieurs axes de symétrie. »
 * Labels (cartes exerciseur) :
 *   - Axes de symétrie d'une figure
 *
 * ── Plan de cartes v2 (2026-08-22, refonte prof : « trop complexe ») ─────
 * La v1 (V/F à trois droites sur figure codée non nommée) cumulait deux
 * difficultés (identifier la figure + évaluer trois droites) et imposait un
 * codage qui alourdissait tout. v2 : figures NUES et NOMMÉES, zéro codage.
 * v2.1 (revue prof 2026-09-18, F1 seulement) : on ne code que ce qui sert à
 * VÉRIFIER une droite — un côté coupé en son milieu par une droite candidate
 * porte le codage du milieu (deux traits identiques sur ses deux moitiés ; deux
 * côtés codés avec le même nombre de traits sont réellement égaux, sinon les
 * nombres diffèrent) ; le triangle isocèle porte en plus ses deux côtés égaux
 * (un trait), sinon « isocèle en quel sommet ? » ; rien d'autre, la nature
 * reste donnée par le nom. Noms des droites sans chevauchement ; pièges du carré
 * plus francs (oblique 25-35°, parallèle décalée d'un tiers de côté).
 *   F1 axe_qcm — « Voici un rectangle. Laquelle de ces droites en
 *      pointillés est un axe de symétrie ? » Choix (d₁)/(d₂)/(d₃)/aucune
 *      des trois. Figures à axe : exactement UNE droite est l'axe, les deux
 *      autres sont les pièges formateurs (diagonale du rectangle, droite
 *      des milieux du losange, oblique issue du sommet de l'isocèle, corde du
 *      cercle, parallèle décalée du carré...). Triangle quelconque / trapèze
 *      quelconque / trapèze rectangle : « aucune des trois » (1/3).
 *      Vivier limité aux figures nommées au cycle 3 (revue prof 2026-09-18) :
 *      trapèze isocèle, cerf-volant et parallélogramme retirés.
 *   F2 planche_multi — six figures nues NOMMÉES sous leur case (« ① carré »,
 *      « ④ parallélogramme »...), multi-sélection « lesquelles ont au moins
 *      un axe ? ». Les propriétés sont connues par le nom, le hasard ne
 *      paie pas, le parallélogramme fait le tri.
 *   F3 completee_qcm — l'objectif CM1 : la même moitié de figure complétée
 *      par symétrie de trois façons sur QUADRILLAGE (A/B/C) — une exacte,
 *      deux avec un sommet décalé d'UN carreau. « Quelle figure est
 *      correctement complétée ? » Vérifiable à l'œil, rigoureux.
 * « Un ou plusieurs » = « au moins un » : PAS de comptage (hors cycle 3
 * pour le cercle), PAS d'habillage. TOUT est vérifié par RÉFLEXION RÉELLE
 * (garde interne du gen + solveur test-side indépendant depuis le SVG).
 */
import {
    randomChoice, randInt, randomChoiceMultiple, shuffle,
    makeQcm,
} from '../../../utils';

const ATTENDU_ID = 1017;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Axes de symétrie d\'une figure';

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1017 : ${message}`);
};

const dataUrl = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;
const elImage = (url) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: url } }] });

const BLEU = '#1E3A8A';
const ROUGE = '#dc2626';
const GRIS_D = '#475569';
// Une couleur par droite candidate (demande prof 2026-09-18 : trois pointillés gris se confondaient
// quand ils se croisaient au centre) ; ni le bleu de la figure ni l'orange des codages.
const COULEURS_D = ['#7C3AED', '#15803D', '#BE185D'];
const NUMS = ['①', '②', '③', '④', '⑤', '⑥'];
const SUBS = ['₁', '₂', '₃'];

// ─── Géométrie ────────────────────────────────────────────────────────────

const rot = (angleDeg) => {
    const a = (angleDeg * Math.PI) / 180;
    return ([x, y]) => [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)];
};
const dist = (P, Q) => Math.hypot(P[0] - Q[0], P[1] - Q[1]);
const milieu = (P, Q) => [(P[0] + Q[0]) / 2, (P[1] + Q[1]) / 2];
const unit = (P, Q) => {
    const dd = dist(P, Q);
    return [(Q[0] - P[0]) / dd, (Q[1] - P[1]) / dd];
};

/** Réflexion du point P par rapport à la droite (AB). */
const reflet = (P, A, B) => {
    const [ux, uy] = [(B[0] - A[0]), (B[1] - A[1])];
    const n2 = ux * ux + uy * uy;
    const t = ((P[0] - A[0]) * ux + (P[1] - A[1]) * uy) / n2;
    const H = [A[0] + t * ux, A[1] + t * uy];
    return [2 * H[0] - P[0], 2 * H[1] - P[1]];
};

/** La droite (AB) est-elle un axe de symétrie du polygone ? */
const estAxePoly = (pts, A, B, tol = 0.9) => {
    const restants = pts.map((p) => [...p]);
    for (const p of pts) {
        const r = reflet(p, A, B);
        let best = -1;
        let bestD = Infinity;
        restants.forEach((q, i) => {
            const d = dist(r, q);
            if (d < bestD) { bestD = d; best = i; }
        });
        if (bestD > tol) return false;
        restants.splice(best, 1);
    }
    return true;
};

const distDroite = (P, A, B) => {
    const [ux, uy] = [B[0] - A[0], B[1] - A[1]];
    return Math.abs(ux * (A[1] - P[1]) - uy * (A[0] - P[0])) / Math.hypot(ux, uy);
};

// ─── Constructeurs + candidats d'axes (coordonnées locales EXACTES) ───────
// F1 : les figures à axe fournissent EXACTEMENT 1 candidat vrai + 2 faux ;
// les figures sans axe, 3 candidats faux.

const BUILDERS = {
    carre: () => {
        const c = randInt(56, 72);
        const pts = [[0, -c], [0, 0], [c, 0], [c, -c]];
        const M = [c / 2, -c / 2];
        const vrai = randomChoice([
            { A: [c / 2, 0], B: [c / 2, -c], axe: true, code: 'mediatrice' },
            { A: [0, -c / 2], B: [c, -c / 2], axe: true, code: 'mediatrice' },
            { A: [0, 0], B: [c, -c], axe: true, code: 'diagonale' },
            { A: [0, -c], B: [c, 0], axe: true, code: 'diagonale' },
        ]);
        // Revue prof 2026-09-18 : l'oblique à 25-35° (18-30° avant) et la parallèle décalée d'un tiers de côté
        // (10-16 px avant) : les trois droites ne forment plus un faisceau indiscernable sur un carré de 60 px.
        const phi = (randomChoice([1, -1]) * randInt(25, 35) * Math.PI) / 180;
        const d = [Math.sin(phi), Math.cos(phi)];
        const t = 0.72 * c;
        const oblique = {
            A: [M[0] - t * d[0], M[1] - t * d[1]],
            B: [M[0] + t * d[0], M[1] + t * d[1]],
            axe: false, code: 'oblique_centre',
        };
        const off = randomChoice([1, -1]) * Math.round(c / 3);
        const decalee = {
            A: [c / 2 + off, 6], B: [c / 2 + off, -c - 6],
            axe: false, code: 'parallele_decalee',
        };
        return { pts, cands: [vrai, oblique, decalee] };
    },
    rectangle: () => {
        const h = randInt(48, 60);
        const w = Math.round(h * (1.4 + Math.random() * 0.4));
        const pts = [[0, -h], [0, 0], [w, 0], [w, -h]];
        const vrai = randomChoice([
            { A: [w / 2, 0], B: [w / 2, -h], axe: true, code: 'mediatrice' },
            { A: [0, -h / 2], B: [w, -h / 2], axe: true, code: 'mediatrice' },
        ]);
        return {
            pts,
            cands: [
                vrai,
                { A: [0, 0], B: [w, -h], axe: false, code: 'diagonale_rectangle' },
                { A: [0, -h], B: [w, 0], axe: false, code: 'diagonale_rectangle' },
            ],
        };
    },
    losange: () => {
        const p = randInt(50, 62);
        const q = Math.round(p * (0.55 + Math.random() * 0.2));
        const pts = [[-p, 0], [0, -q], [p, 0], [0, q]];
        const vrai = randomChoice([
            { A: [-p, 0], B: [p, 0], axe: true, code: 'diagonale' },
            { A: [0, -q], B: [0, q], axe: true, code: 'diagonale' },
        ]);
        return {
            pts,
            cands: [
                vrai,
                { A: milieu(pts[0], pts[1]), B: milieu(pts[2], pts[3]), axe: false, code: 'milieux_losange' },
                { A: milieu(pts[1], pts[2]), B: milieu(pts[3], pts[0]), axe: false, code: 'milieux_losange' },
            ],
        };
    },
    triangle_iso: () => {
        let pts = [[32, -66], [0, 0], [64, 0]];
        for (let essai = 0; essai < 30; essai++) {
            const base = randInt(56, 74);
            const h = Math.round(base * (0.8 + Math.random() * 0.45));
            const cand = [[base / 2, -h], [0, 0], [base, 0]];
            if (Math.abs(base - dist(cand[0], cand[1])) < 0.2 * dist(cand[0], cand[1])) continue;
            pts = cand;
            break;
        }
        return {
            pts,
            egaux: [0, 2],   // les deux côtés issus du sommet principal pts[0]
            cands: [
                { A: pts[0], B: milieu(pts[1], pts[2]), axe: true, code: 'axe_iso' },
                // Pièges sans milieu (revue prof 2026-09-18) : les médianes latérales obligeaient à coder les milieux
                // des côtés égaux par-dessus leur trait d'égalité, illisible. Une oblique issue du sommet principal
                // (elle ne coupe pas la base en son milieu) et une parallèle à l'axe décalée (elle ne passe pas par le sommet).
                (() => { const M = milieu(pts[1], pts[2]); const u = unit(pts[0], M); const v = [-u[1], u[0]]; const dec = randomChoice([1, -1]) * 0.28 * dist(pts[1], pts[2]); return { A: pts[0], B: [M[0] + dec * v[0], M[1] + dec * v[1]], axe: false, code: 'oblique_sommet' }; })(),
                (() => { const M = milieu(pts[1], pts[2]); const u = unit(pts[0], M); const v = [-u[1], u[0]]; const dec = randomChoice([1, -1]) * 0.36 * dist(pts[1], pts[2]); return { A: [pts[0][0] + dec * v[0], pts[0][1] + dec * v[1]], B: [M[0] + dec * v[0], M[1] + dec * v[1]], axe: false, code: 'parallele_decalee' }; })(),   // 0,36 : ni le milieu d'un côté égal (0,25), ni le sommet (0,5)
            ],
        };
    },
    triangle_equi: () => {
        const a = randInt(60, 76);
        const h = (a * Math.sqrt(3)) / 2;
        const pts = [[a / 2, -h], [0, 0], [a, 0]];
        const i = randInt(0, 2);
        const axe = {
            A: pts[i], B: milieu(pts[(i + 1) % 3], pts[(i + 2) % 3]),
            axe: true, code: 'axe_equilateral',
        };
        const mids = shuffle(pts.map((p, j) => ({
            A: milieu(p, pts[(j + 1) % 3]),
            B: milieu(p, pts[(j + 2) % 3]),
            axe: false, code: 'droite_des_milieux',
        }))).slice(0, 2);
        return { pts, cands: [axe, ...mids] };
    },
    cercle: () => {
        const r = randInt(38, 48);
        const deg = randInt(0, 179);
        const a = (deg * Math.PI) / 180;
        const d = [Math.cos(a), Math.sin(a)];
        const axe = { A: [-1.12 * r * d[0], -1.12 * r * d[1]], B: [1.12 * r * d[0], 1.12 * r * d[1]], axe: true, code: 'par_le_centre' };
        const corde = (degC) => {
            const ca = (degC * Math.PI) / 180;
            const cd = [Math.cos(ca), Math.sin(ca)];
            const off = (0.35 + Math.random() * 0.2) * r;
            const C0 = [-off * cd[1], off * cd[0]];
            const demi = Math.sqrt(r * r - off * off) * 0.98;
            return {
                A: [C0[0] - demi * cd[0], C0[1] - demi * cd[1]],
                B: [C0[0] + demi * cd[0], C0[1] + demi * cd[1]],
                axe: false, code: 'corde_hors_centre',
            };
        };
        return { cercleR: r, cands: [axe, corde(randInt(0, 179)), corde(randInt(0, 179))] };
    },
    trapeze_rect: () => {
        // Trapèze rectangle (nommé en 6e, revue prof 2026-09-18) : deux angles droits à gauche, aucun axe.
        // Pièges : la droite des milieux des bases, une diagonale, la perpendiculaire aux bases par le milieu de la petite base.
        const w1 = randInt(76, 96);
        const h = randInt(44, 58);
        const w2 = Math.round(w1 * (0.45 + Math.random() * 0.3));
        const pts = [[0, 0], [w1, 0], [w2, -h], [0, -h]];
        return {
            pts,
            cands: shuffle([
                { A: [w1 / 2, 0], B: [w2 / 2, -h], axe: false, code: 'milieux_bases' },
                { A: [0, 0], B: [w2, -h], axe: false, code: 'diagonale' },
                { A: [w2 / 2, 8], B: [w2 / 2, -h - 8], axe: false, code: 'perpendiculaire_petite_base' },
            ]),
        };
    },
    trapeze_qcq: () => {
        for (let essai = 0; essai < 30; essai++) {
            const w1 = randInt(76, 96);
            const h = randInt(44, 58);
            const a = randInt(14, 26);
            const b = a + randInt(34, 48);
            if (b >= w1 - 8) continue;
            if (Math.abs(a - (w1 - b)) < 10) continue;   // sinon quasi isocèle
            const pts = [[0, 0], [w1, 0], [b, -h], [a, -h]];
            return {
                pts,
                cands: shuffle([
                    { A: [w1 / 2, 0], B: [(a + b) / 2, -h], axe: false, code: 'milieux_bases' },
                    { A: [0, 0], B: [b, -h], axe: false, code: 'diagonale' },
                    { A: [w1, 0], B: [a, -h], axe: false, code: 'diagonale' },
                ]),
            };
        }
        const pts = [[0, 0], [88, 0], [60, -50], [18, -50]];
        return {
            pts,
            cands: [
                { A: [44, 0], B: [39, -50], axe: false, code: 'milieux_bases' },
                { A: [0, 0], B: [60, -50], axe: false, code: 'diagonale' },
                { A: [88, 0], B: [18, -50], axe: false, code: 'diagonale' },
            ],
        };
    },
    triangle_qcq: () => {
        let pts = [[0, 0], [72, 0], [30, -30]];
        for (let essai = 0; essai < 40; essai++) {
            const w = randInt(58, 78);
            const x3 = randInt(-14, w + 14);
            const h = randInt(36, 58);
            const cand = [[0, 0], [w, 0], [x3, -h]];
            const cotes = [dist(cand[0], cand[1]), dist(cand[1], cand[2]), dist(cand[2], cand[0])];
            const paireProche = cotes.some((x, i) => cotes.some((y, j) =>
                i < j && Math.abs(x - y) < 0.12 * Math.max(x, y)));
            if (paireProche) continue;
            pts = cand;
            break;
        }
        return {
            pts,
            cands: shuffle(pts.map((p, i) => ({
                A: p,
                B: milieu(pts[(i + 1) % 3], pts[(i + 2) % 3]),
                axe: false, code: 'mediane_quelconque',
            }))),
        };
    },
};

// Vivier limité aux figures NOMMÉES au cycle 3 (revue prof 2026-09-18) : le trapèze isocèle, le cerf-volant
// et le parallélogramme n'y sont pas (le parallélogramme est défini en 5e) ; le trapèze rectangle, nommé en 6e,
// prend la place de cas négatif.
const POSITIFS = ['carre', 'rectangle', 'losange', 'triangle_iso', 'triangle_equi', 'cercle'];
const NEGATIFS = ['triangle_qcq', 'trapeze_qcq', 'trapeze_rect'];
const POOL_F1 = [...POSITIFS, ...NEGATIFS];   // uniforme : « aucune » = 3/9

/** Les figures sont NOMMÉES (refonte prof 2026-08-22) : l'élève raisonne
 *  sur les propriétés connues de la figure annoncée — zéro codage. */
const NOMS = {
    carre: 'un carré',
    rectangle: 'un rectangle',
    losange: 'un losange',
    triangle_iso: 'un triangle isocèle',
    triangle_equi: 'un triangle équilatéral',
    cercle: 'un cercle',
    trapeze_rect: 'un trapèze rectangle',
    triangle_qcq: 'un triangle quelconque',
    trapeze_qcq: 'un trapèze quelconque',
};
const NOMS_COURTS = {
    carre: 'carré',
    rectangle: 'rectangle',
    losange: 'losange',
    triangle_iso: 'triangle isocèle',
    triangle_equi: 'triangle équilatéral',
    cercle: 'cercle',
    trapeze_rect: 'trapèze rectangle',
    triangle_qcq: 'triangle quelconque',
    trapeze_qcq: 'trapèze quelconque',
};

// ─── Pose et rendu (figure seule, avec ou sans droites) ───────────────────

/** Tourne figure + droites, étend les droites, recadre le tout. */
const poserFigure1017 = (fig) => {
    const angle = randInt(0, 359);
    const r = rot(angle);
    const pts = fig.pts ? fig.pts.map(r) : null;
    const cands = (fig.cands || []).map((c) => ({ ...c, A: r(c.A), B: r(c.B) }));
    const lignes = cands.map((c) => {
        const u = unit(c.A, c.B);
        return {
            ...c,
            x1: c.A[0] - 16 * u[0], y1: c.A[1] - 16 * u[1],
            x2: c.B[0] + 16 * u[0], y2: c.B[1] + 16 * u[1],
            lu: u,
        };
    });
    const R = fig.cercleR || 0;
    const tous = [
        ...(pts || [[-R, -R], [R, R], [-R, R], [R, -R]]),
        ...lignes.flatMap((l) => [[l.x1, l.y1], [l.x2, l.y2]]),
    ];
    const marge = 40;   // couvre l'étiquette (dᵢ) posée 10 px au-delà de la droite, décalée de 14 px au besoin
    const dx = marge - Math.min(...tous.map((p) => p[0]));
    const dy = marge - Math.min(...tous.map((p) => p[1]));
    const dep = ([x, y]) => [x + dx, y + dy];
    const W = Math.ceil(Math.max(...tous.map((p) => p[0])) + dx + marge);
    const H = Math.ceil(Math.max(...tous.map((p) => p[1])) + dy + marge);
    return {
        pts: pts ? pts.map(dep) : null,
        centre: dep([0, 0]),
        lignes: lignes.map((l) => ({
            ...l,
            x1: l.x1 + dx, y1: l.y1 + dy, x2: l.x2 + dx, y2: l.y2 + dy,
        })),
        W, H,
    };
};

/** Un trait de codage perpendiculaire au côté (P,Q), centré au point C. */
const ORANGE_MILIEU = '#b45309';   // codage des milieux dans une couleur a part (demande prof 18/09 : les cerner d un coup d oeil)
const traitCodage = (P, Q, C, k, n, couleur = BLEU) => {
    const u = unit(P, Q); const v = [-u[1], u[0]];
    const t = (k - (n - 1) / 2) * 4;   // n traits espacés de 4 px le long du côté
    const c = [C[0] + u[0] * t, C[1] + u[1] * t];
    return `  <line x1="${(c[0] + 4.5 * v[0]).toFixed(1)}" y1="${(c[1] + 4.5 * v[1]).toFixed(1)}" x2="${(c[0] - 4.5 * v[0]).toFixed(1)}" y2="${(c[1] - 4.5 * v[1]).toFixed(1)}" stroke="${couleur}" stroke-width="1.8" data-codage="1"/>`;
};

/**
 * Codages de la carte « laquelle est un axe ? » (revue prof 2026-09-18) : on ne code que ce qui sert à
 * vérifier une droite.
 *  - Un côté coupé EN SON MILIEU par une droite candidate (vraie ou piège) reçoit le codage du milieu : le
 *    même nombre de traits sur ses deux moitiés. Deux côtés codés reçoivent le même nombre de traits
 *    seulement s'ils sont réellement de même longueur (sinon 1, 2, 3 par longueur), pour ne jamais
 *    affirmer une égalité fausse.
 *  - Le triangle isocèle code en plus ses deux côtés égaux (un trait), sans quoi le sommet principal
 *    n'est pas identifiable ; ses milieux codés partent alors de deux traits.
 *  - Une droite qui passe par des sommets n'a rien à coder, les sommets se voient.
 *  - Les codages de milieu sont en orange, ceux d'égalité de côtés en bleu : on les cerne d'un coup d'œil.
 */
const codagesF1 = (pose, fig) => {
    const pts = pose.pts; const n = pts.length; const out = [];
    const egaux = fig.egaux || [];
    egaux.forEach((k) => {
        const P = pts[k]; const Q = pts[(k + 1) % n];
        out.push(traitCodage(P, Q, milieu(P, Q), 0, 1));
    });
    // côtés dont le milieu est sur une droite candidate
    const cibles = [];
    for (let k = 0; k < n; k++) {
        const P = pts[k]; const Q = pts[(k + 1) % n]; const M = milieu(P, Q);
        if (pose.lignes.some((l) => distDroite(M, [l.x1, l.y1], [l.x2, l.y2]) < 0.6)) cibles.push({ k, P, Q, M, L: dist(P, Q) });
    }
    // nombre de traits par longueur : égaux → même nombre ; l'isocèle réserve le trait simple à ses côtés égaux
    const longueurs = [];
    cibles.forEach((c) => { if (!longueurs.some((L) => Math.abs(L - c.L) < 0.8)) longueurs.push(c.L); });
    const depart = egaux.length ? 2 : 1;
    cibles.forEach((c) => {
        const nb = depart + longueurs.findIndex((L) => Math.abs(L - c.L) < 0.8);
        const M1 = milieu(c.P, c.M); const M2 = milieu(c.M, c.Q);
        for (let i = 0; i < nb; i++) { out.push(traitCodage(c.P, c.Q, M1, i, nb, ORANGE_MILIEU)); out.push(traitCodage(c.P, c.Q, M2, i, nb, ORANGE_MILIEU)); }
    });
    return out;
};

const svgFigureSeule = (fig) => {
    const pose = poserFigure1017(fig);
    const p = [];
    p.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${pose.W} ${pose.H}" width="${Math.min(300, pose.W)}" role="img"`);
    p.push('  aria-label="Une figure géométrique avec trois droites en pointillés numérotées.">');
    if (pose.pts) {
        p.push(`  <polygon points="${pose.pts.map((pt) => `${pt[0].toFixed(1)},${pt[1].toFixed(1)}`).join(' ')}" fill="#eff6ff" stroke="${BLEU}" stroke-width="2.2" data-fig="0"/>`);
        p.push(...codagesF1(pose, fig));
    } else {
        p.push(`  <circle cx="${pose.centre[0].toFixed(1)}" cy="${pose.centre[1].toFixed(1)}" r="${fig.cercleR}" fill="#eff6ff" stroke="${BLEU}" stroke-width="2.2" data-fig="0"/>`);
        p.push(`  <circle cx="${pose.centre[0].toFixed(1)}" cy="${pose.centre[1].toFixed(1)}" r="2.2" fill="${BLEU}"/>`);
    }
    // Noms des droites : au bout 2 par défaut ; si le nom tombe à moins de 24 px d'un nom déjà posé
    // (droites presque parallèles et proches), on passe au bout 1, puis on décale perpendiculairement
    // (revue prof 2026-09-18 : (d₂) et (d₃) se superposaient sur le carré).
    const poses = [];
    pose.lignes.forEach((l, i) => {
        p.push(`  <line x1="${l.x1.toFixed(1)}" y1="${l.y1.toFixed(1)}" x2="${l.x2.toFixed(1)}" y2="${l.y2.toFixed(1)}" stroke="${COULEURS_D[i] || GRIS_D}" stroke-width="1.9" stroke-dasharray="6 4" data-droite="${i}"/>`);
        const n = [-l.lu[1], l.lu[0]];
        const candidats = [
            [l.x2 + 10 * l.lu[0], l.y2 + 10 * l.lu[1]],
            [l.x1 - 10 * l.lu[0], l.y1 - 10 * l.lu[1]],
            [l.x2 + 10 * l.lu[0] + 14 * n[0], l.y2 + 10 * l.lu[1] + 14 * n[1]],
            [l.x1 - 10 * l.lu[0] + 14 * n[0], l.y1 - 10 * l.lu[1] + 14 * n[1]],
            [l.x2 + 10 * l.lu[0] - 14 * n[0], l.y2 + 10 * l.lu[1] - 14 * n[1]],
            [l.x1 - 10 * l.lu[0] - 14 * n[0], l.y1 - 10 * l.lu[1] - 14 * n[1]],
            [l.x2 + 10 * l.lu[0] + 28 * n[0], l.y2 + 10 * l.lu[1] + 28 * n[1]],
            [l.x2 + 10 * l.lu[0] - 28 * n[0], l.y2 + 10 * l.lu[1] - 28 * n[1]],
        ];
        const libre = (c) => poses.every((q) => Math.hypot(q[0] - c[0], q[1] - c[1]) >= 24);
        const [lx, ly] = candidats.find(libre) || candidats[0];
        poses.push([lx, ly]);
        p.push(`  <text x="${lx.toFixed(1)}" y="${(ly + 4.5).toFixed(1)}" font-size="12.5" font-weight="700" fill="${COULEURS_D[i] || GRIS_D}" text-anchor="middle" font-family="system-ui,sans-serif">(d${SUBS[i]})</text>`);
    });
    p.push('</svg>');
    return p.join('\n');
};

// ─── F1 : « laquelle de ces droites est un axe ? » ────────────────────────

const AUCUNE = 'aucune des trois';

const genAxeQcm = () => {
    const type = randomChoice(POOL_F1);
    const fig = BUILDERS[type]();
    const cands = shuffle(fig.cands);
    // garde : chaque verdict re-vérifié par réflexion réelle, et UNE seule
    // droite vraie sur les figures à axe (aucune sur les autres)
    cands.forEach((c) => {
        const verif = fig.pts
            ? estAxePoly(fig.pts, c.A, c.B)
            : distDroite([0, 0], c.A, c.B) < 0.5;
        garde(verif === c.axe, `verdict incohérent (${type}/${c.code})`);
    });
    const nbVrais = cands.filter((c) => c.axe).length;
    garde(nbVrais === (POSITIFS.includes(type) ? 1 : 0), `nb d'axes inattendu (${type})`);
    const svg = svgFigureSeule({ ...fig, cands });
    const bonne = nbVrais === 1 ? `(d${SUBS[cands.findIndex((c) => c.axe)]})` : AUCUNE;
    const choix = [...SUBS.map((s) => `(d${s})`), AUCUNE];
    const payload = makeQcm(
        choix,
        bonne,
        `<p><b>Voici ${NOMS[type]}. Laquelle de ces droites en pointillés est un axe de symétrie de la figure ?</b></p><p>{{element:0}}</p>`,
        { ...opts(L1), elements_additionnels: elImage(dataUrl(svg)) },
        false,
    );
    payload.contenu = { ...payload.contenu, sousType: `axe_${type}` };
    payload._meta.params = { sousType: `axe_${type}`, codes: cands.map((c) => c.code) };
    return payload;
};

// ─── F2 : la planche nommée en multi-sélection ────────────────────────────

const CASES6 = [[0, 0], [140, 0], [280, 0], [0, 140], [140, 140], [280, 140]];

const poserDansCase = (fig, cx, cy) => {
    if (fig.cercleR) {
        const R = Math.min(fig.cercleR, 38);
        return { centre: [cx + 70, cy + 62], R };
    }
    const r = rot(randInt(0, 359));
    let t = fig.pts.map(r);
    const minX = Math.min(...t.map((p) => p[0]));
    const minY = Math.min(...t.map((p) => p[1]));
    t = t.map(([x, y]) => [x - minX, y - minY]);
    const W = Math.max(...t.map((p) => p[0]));
    const H = Math.max(...t.map((p) => p[1]));
    const s = Math.min(1, 104 / W, 84 / H);
    const ox = cx + 18 + (104 - W * s) / 2;
    const oy = cy + 14 + (84 - H * s) / 2;
    return { pts: t.map(([x, y]) => [ox + x * s, oy + y * s]) };
};

const svgPlanche1017 = (types) => {
    const p = [];
    p.push('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 420 280" width="340" role="img"');
    p.push('  aria-label="Six figures géométriques nommées, numérotées de 1 à 6, dans des positions variées.">');
    types.forEach((type, k) => {
        const [cx, cy] = CASES6[k];
        const fig = BUILDERS[type]();
        const pose = poserDansCase(fig, cx, cy);
        if (pose.centre) {
            p.push(`  <circle cx="${pose.centre[0]}" cy="${pose.centre[1]}" r="${pose.R}" fill="#eff6ff" stroke="${BLEU}" stroke-width="2" data-fig="${k}"/>`);
            p.push(`  <circle cx="${pose.centre[0]}" cy="${pose.centre[1]}" r="2" fill="${BLEU}"/>`);
        } else {
            p.push(`  <polygon points="${pose.pts.map((pt) => `${pt[0].toFixed(1)},${pt[1].toFixed(1)}`).join(' ')}" fill="#eff6ff" stroke="${BLEU}" stroke-width="2" data-fig="${k}"/>`);
        }
        p.push(`  <text x="${cx + 70}" y="${cy + 124}" font-size="10.5" font-weight="700" fill="#111827" text-anchor="middle" font-family="system-ui,sans-serif" data-num="${k}">${NUMS[k]} ${NOMS_COURTS[type]}</text>`);
    });
    p.push('</svg>');
    return p.join('\n');
};

const genPlancheMulti = () => {
    const nPos = randInt(3, 5);
    const types = shuffle([
        ...randomChoiceMultiple(POSITIFS, nPos),
        ...randomChoiceMultiple(NEGATIFS, 6 - nPos),
    ]);
    const svg = svgPlanche1017(types);
    const choix = types.map((_, k) => `Figure ${NUMS[k]}`);
    const bonnes = types.map((t, k) => (POSITIFS.includes(t) ? `Figure ${NUMS[k]}` : null)).filter(Boolean);
    garde(bonnes.length === nPos, 'planche multi incohérente');
    const payload = makeQcm(
        choix,
        bonnes,
        '<p><b>Parmi ces six figures, lesquelles ont au moins un axe de symétrie ?</b></p><p>{{element:0}}</p>',
        { ...opts(L1), elements_additionnels: elImage(dataUrl(svg)) },
        true,
    );
    payload.contenu = { ...payload.contenu, sousType: 'planche_multi' };
    payload._meta.params = { sousType: 'planche_multi', types, nPos };
    return payload;
};

// ─── F3 : la figure complétée sur quadrillage ─────────────────────────────

const U3 = 12;   // le carreau

/** La demi-figure (en carreaux) : chaîne x ≤ -1 entre deux points d'axe. */
const tirerMoitie = () => [
    [0, 0],
    [-randInt(1, 3), 1],
    [-randInt(2, 3), 2],
    [-randInt(1, 3), 3],
    [0, 4],
];

/** Le polygone complet : la moitié + son image (exacte ou décalée). */
const completer = (moitie, erreur) => {
    const droite = moitie.slice(1, -1).reverse().map(([x, y], idx) => {
        const i = moitie.length - 2 - idx;   // index dans la moitié
        let mx = -x;
        if (erreur && erreur.i === i) {
            mx += (mx <= 1 && erreur.dir === -1) ? 1 : erreur.dir;
        }
        return [mx, y];
    });
    return [...moitie, ...droite];
};

const svgCompletee = (moitie, variantes) => {
    const p = [];
    p.push('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 336 96" width="336" role="img"');
    p.push('  aria-label="Trois propositions A, B et C : la même moitié de figure complétée par symétrie sur quadrillage, une seule est exacte.">');
    variantes.forEach((v, k) => {
        const px = 6 + k * 112;
        const ax = px + 52;
        p.push(`  <text x="${px + 4}" y="${13}" font-size="13" font-weight="800" fill="#111827" font-family="system-ui,sans-serif">${'ABC'[k]}</text>`);
        for (let i = -4; i <= 4; i++) {
            p.push(`  <line x1="${ax + i * U3}" y1="18" x2="${ax + i * U3}" y2="92" stroke="#e2e8f0" stroke-width="1"/>`);
        }
        for (let j = 0; j <= 6; j++) {
            p.push(`  <line x1="${ax - 4 * U3}" y1="${18 + j * U3}" x2="${ax + 4 * U3}" y2="${18 + j * U3}" stroke="#e2e8f0" stroke-width="1"/>`);
        }
        const pts = completer(moitie, v.erreur);
        p.push(`  <polygon points="${pts.map(([x, y]) => `${ax + x * U3},${24 + y * U3}`).join(' ')}" fill="#dbeafe" fill-opacity="0.85" stroke="${BLEU}" stroke-width="2" data-fig="${k}"/>`);
        p.push(`  <line x1="${ax}" y1="16" x2="${ax}" y2="94" stroke="${ROUGE}" stroke-width="1.6" stroke-dasharray="5 4" data-axe="${k}"/>`);
    });
    p.push('</svg>');
    return p.join('\n');
};

const genCompleteeQcm = () => {
    const moitie = tirerMoitie();
    // deux erreurs sur des sommets DISTINCTS (le clamp anti-axe pourrait
    // rendre identiques deux directions opposées sur un même sommet en x=1)
    const i1 = randInt(1, 3);
    const i2 = randomChoice([1, 2, 3].filter((i) => i !== i1));
    const variantes = shuffle([
        { erreur: null },
        { erreur: { i: i1, dir: randomChoice([1, -1]) } },
        { erreur: { i: i2, dir: randomChoice([1, -1]) } },
    ]);
    const kBonne = variantes.findIndex((v) => !v.erreur);
    // garde : la variante exacte est symétrique, les autres non (réflexion réelle)
    variantes.forEach((v, k) => {
        const pts = completer(moitie, v.erreur).map(([x, y]) => [x * U3, y * U3]);
        garde(estAxePoly(pts, [0, 0], [0, 10]) === !v.erreur, 'complétion incohérente');
    });
    const svg = svgCompletee(moitie, variantes);
    const bonne = `Figure ${'ABC'[kBonne]}`;
    const payload = makeQcm(
        ['Figure A', 'Figure B', 'Figure C'],
        bonne,
        '<p><b>On a complété la même moitié de figure par symétrie par rapport à l\'axe en pointillés, de trois façons. Quelle figure est correctement complétée ?</b></p><p>{{element:0}}</p>',
        { ...opts(L1), elements_additionnels: elImage(dataUrl(svg)) },
        false,
    );
    payload.contenu = { ...payload.contenu, sousType: 'completee' };
    payload._meta.params = { sousType: 'completee', erreurs: variantes.map((v) => v.erreur) };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        '« un ou plusieurs » = « au moins un » : JAMAIS de comptage d\'axes (arbitrage prof 2026-08-22, « une infinité » hors cycle 3)',
        'v2 (refonte prof 2026-08-22) : figures NOMMÉES, l\'élève raisonne sur les propriétés de la figure annoncée ; v2.1 (2026-09-18) : sur la carte axe_qcm, seuls les milieux coupés par une droite candidate sont codés (orange), plus les côtés égaux de l\'isocèle (bleu)',
        'chaque verdict d\'axe est VÉRIFIÉ par réflexion réelle des sommets (garde interne du gen + solveur test-side indépendant)',
        'les cas négatifs qui discriminent (triangle quelconque, trapèze quelconque, trapèze rectangle) sont systématiquement dans les viviers ; vivier limité aux figures nommées au cycle 3 (revue 2026-09-18)',
    ],
    axe_qcm: {
        fixes: {
            diagonale_rectangle: 'LE piège formateur (coupe en deux parties égales mais le pliage ne superpose pas) — en contraste avec la diagonale du carré (VRAIE)',
            milieux_losange: 'le miroir du piège rectangle',
            oblique_sommet: 'l\'isocèle n\'a qu\'UN axe : par le sommet principal ET par le milieu de la base (revue 2026-09-18, remplace la médiane latérale)',
            corde_hors_centre: 'passer par le cercle ne suffit pas : il faut passer par le centre',
            parallele_decalee: 'parallèle à l\'axe (carré, isocèle) mais pas au milieu, ou pas par le sommet',
            aucune: '« aucune des trois » vivante 1/3 (triangle quelconque, trapèze quelconque, trapèze rectangle)',
        },
    },
    completee_qcm: {
        fixes: { erreurs: 'un sommet de la complétion décalé d\'UN carreau (deux variantes distinctes) — l\'erreur fine voulue par le cadrage, vérifiable à l\'œil sur quadrillage' },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    axe_qcm: genAxeQcm,
    planche_multi: genPlancheMulti,
    completee_qcm: genCompleteeQcm,
};

const FORMAT_LABELS = {
    axe_qcm: 'Laquelle de ces droites est un axe ?',
    planche_multi: 'Trier la planche',
    completee_qcm: 'La figure complétée',
};

export const CATEGORIES = [
    {
        id: 'axes',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
